import {
  PaymentSystem,
  Product as ProductDetails,
  Currency,
  ModeCardForm,
} from "@web-solutions/react-billing/constants";
import {
  RemoteConfig as ManageRemoteConfig,
  INITIAL_CONFIG as MANAGE_INITIAL_CONFIG,
} from "@web-solutions/manage-subscription/src/manage/constants/remote-config";
import { type LString } from "@web-solutions/module-localization";
import { LinkTarget } from "@web-solutions/module-attribution";
import { type SpecialOfferMode } from "@web-solutions/core/interfaces/specialOffer";
import type { Theme } from "@web-solutions/core/interfaces/theme";
import type { NoFundsOffer } from "@web-solutions/core/interfaces/remote-config";
import type { ProductAttachment } from "@web-solutions/core/interfaces/products";

import { ImageSource } from "core/interfaces/images";

import { REDIRECT_TO_CHROME } from "core/utils/redirect-to-chrome";

import {
  PriceAfterTrialType,
  ButtonPlacementType,
  ButtonBehavior,
  CardFormLayout,
  ButtonConfig,
} from "core/payment/constants";

import {
  INITIAL_CONFIG_UPSALES,
  RemoteConfigUpsales,
} from "core/upsale/constants/remote-config";

import {
  ModeApplePay,
  ModeGooglePay,
  ModePayPal,
  InertialApplePay,
  ErrorPopupType,
  ModeMercadoPago,
} from "./billing";

export interface PolicyLinks {
  termsOfUse: string;
  privacy: string;
  moneyBack: string;
  billingTerms: string;
  address?: string;
  phone?: string;
  legalEntity?: string;
  descriptor?: string;
}

const POLICY_LINKS: PolicyLinks = {
  termsOfUse: "/terms-of-use",
  privacy: "/privacy-policy",
  moneyBack: "/money-back-policy",
  billingTerms: "/billing-terms",
};

const BTNS_CONFIGS: {
  [key in ButtonPlacementType]: ButtonConfig;
} = {
  [ButtonPlacementType.PAYMENT]: {
    title: "",
    uppercaseTitle: false,
  },
  [ButtonPlacementType.PLANS]: {
    behavior: ButtonBehavior.MODAL,
    title: "",
    uppercaseTitle: false,
    enabledFlareAnimation: true,
  },
  [ButtonPlacementType.FIXED]: {
    behavior: ButtonBehavior.SCROLL,
    title: "",
    uppercaseTitle: false,
    enabledFlareAnimation: true,
  },
};

export { LString };

export interface CurrencyConfig {
  notActivePrice: string;
  activePrice: string;
  price: number;
  discount: string;
}

export interface BaseOfferProductTypes<T> {
  title: LString;
  subTitle: string;
  notActivePrice: string;
  activePrice: string;
  discount: LString;
  price: number;
  currency: Currency;
  id: T;
  EUR?: CurrencyConfig;
  CAD?: CurrencyConfig;
  AUD?: CurrencyConfig;
  USD?: CurrencyConfig;
}

export type ProductConfig = {
  id?: string;
  paypalPlanId?: string;
  recurlyId?: string;
  solidgateId?: string;
  stripeId?: string;
  productCode?: string;
  paddleId?: string;
  /** TailoredPay (NMI) plan_id — echoed back to POST /nmi/checkout. */
  tailoredpayId?: string;
  /**
   * TailoredPay trial params. `GET /nmi/plans` only returns the recurring plan
   * ({ id, name, amount, currency, period }); what is charged today and when the
   * plan starts are decided by us and passed to the checkout.
   * `trialAmount: 0` means a free trial.
   */
  trialAmount?: number;
  trialDays?: number;
  title?: LocalizedString;
  name?: LString;
  subTitle?: LocalizedString;
  descriptionText?: LString;
  badgeTitle?: LString;
  badgeCentered?: boolean;
  discount?: number;
  trialDiscount?: number;
  default?: boolean;
  textLineThrough?: string | LString;
  accentTitle?: LString;
  accentSubTitle?: LString;
  accentTextLineThrough?: LString;
  aboutTrialText?: LString;
  afterTrialText?: LString;
  showAfterTrialPrice?: boolean;
  sold?: { left: number; total: number; hiddenSubTitles?: boolean };
  promocode?: {
    discount: string;
    discountPrice: string;
  };
  smallBadges?: string[];
  trialsLeftBadge?: boolean;
  buttonTitle?: LString;
  attachment?: ProductAttachment;
  perWeekTitle?: string;
  priceStartTrial?: {
    price: LocalizedString;
    billingPeriod: LocalizedString;
    billedAmount: string;
    totalAmount: string;
    buttonLabel: string;
    plan_end_days: string;
    /**
     * Amount charged today, as a number. Declared so the generated schema keeps it:
     * TailoredPay reads the trial from here (`plan_end_days` too) when the product
     * has no explicit `trialAmount`/`trialDays`, so what is charged matches what the
     * funnel displays.
     */
    trialAmount?: number;
  };
  perWeekText: LocalizedString;
  paid_status: "free" | "promo" | "paid" | "trail";
  plan_end_days: string;
  productTitle?: LocalizedString;
  trialText?: LocalizedString;
};

type AdditionalFields = {
  email?: {
    label: string;
    type: string;
    isRequired: boolean;
  };
  cardholder?: boolean;
  zip?: boolean;
};

export enum InjectedPaymentMethod {
  APPLEPAY = "applepay",
  PAYPAL = "paypal",
  CARD = "card",
  GOOGLEPAY = "googlepay",
  APPLEPAY_PAYPAL = "applepay_paypal",
  GOOGLEPAY_PAYPAL = "googlepay_paypal",
  APPLEPAY_GOOGLEPAY = "applepay_googlepay",
}

export enum PaymentModalType {
  BASE = "base",
  BOTTOM_INJECTED = "bottom_injected",
  TOP_INJECTED = "top_injected",
  SIMPLE = "simple",
  SIMPLE_TITLE = "simple_title",
}

export enum PlanType {
  DEFAULT = "default",
  SINGLE = "single",
  HORIZONTAL = "horizontal",
  PDF = "pdf",
  TICKET = "ticket",
  MODAL = "modal",
}

interface Quiz {
  magic: {
    subTitle: LocalizedString;
  };
  answersTitle: LocalizedString;
  buttonTextResults: LocalizedString;
}

export interface LocalizedString {
  en: string;
  es: string;
  fr: string;
  pt: string;
  de: string;
  tr: string;
  it: string;
  cs: string;
  ro: string;
  pl: string;
  id: string;
}
export interface LocalizedArray {
  en: string[];
  es: string[];
  fr: string[];
  pt: string[];
  de: string[];
  tr: string[];
  it: string[];
  cs: string[];
  ro: string[];
  pl: string[];
  id: string[];
}

interface Email {
  title: LocalizedString;
  button: LocalizedString;
  placeholder: LocalizedString;
  notificationText: LocalizedString;
}

export type PriceTypes = "today" | "week" | "day";

export type Nullable<T> = { [K in keyof T]: T[K] | null };

export interface RemoteConfig<FlowStep = string, ChatFlow = string>
  extends ManageRemoteConfig,
    RemoteConfigUpsales {
  theme: Theme;

  oneClickApplePayGooglePayMode: "1click" | "native" | "modal" | "native+modal";
  isOneClickFootnote: boolean;
  isDownloadPdfInBrowser: boolean;
  isCvvTooltip: boolean;
  displayVAT: boolean;
  configVAT: {
    withGeo: boolean;
    view: "simple" | "table";
  };
  linkTarget: LinkTarget;
  planType: PlanType;
  plansFeatures: string[];
  paymentDescriptionType: "old" | "new" | "icons";
  showTimerInPlanBadge: boolean;
  paymentModalEmailEnabled?: boolean;
  paymentProject: string;
  paymentSystem: PaymentSystem;
  paymentErrorRetries: {
    enabledFunds: boolean;
    enabled3ds: boolean;
    error3dsCodes: string[];
  };
  paymentModalHeader: {
    title?: LString;
    subtitle?: LString;
  };
  simpleTitleModal: {
    type: "default" | "short";
    titleEnabled: boolean;
    text: LString;
    fontSize?: number;
    withTotalPriceUnder?: boolean;
  };
  paymentSystemReserve: PaymentSystem | "";
  products: ProductConfig[];
  productsDetails: Array<
    Omit<ProductDetails, "period"> & { period: "ONETIME" }
  >;
  additionalFields: AdditionalFields;
  policyLinks: PolicyLinks;
  address: LocalizedString;
  buttonsConfigs: typeof BTNS_CONFIGS;
  priceAfterTrialType: PriceAfterTrialType;
  productsTitle: LocalizedString;
  showAfterTrialPrice: boolean;
  showPricesBlock: boolean;
  showPriceDiscount: boolean;
  openPaymentModalOnProductClick: boolean;
  isShowSafeCheckout: boolean;
  discountTime: number;
  modePayPal: ModePayPal;
  modeApplePay: ModeApplePay;
  inertialApplePay: InertialApplePay;
  modeGooglePay: ModeGooglePay;
  modeMercadoPago: ModeMercadoPago;
  cardFormCollapsed: boolean;
  cardFormLayout: CardFormLayout;
  showPaymentSafeAtPlans: boolean;
  showTermsAtPlans: boolean;
  totalPriceType: PriceTypes;
  isInjectedPaymentMethodOnPage: boolean;
  redirectToSuccessOnDecline: boolean;
  showPaymentProcessingOverlay: boolean;
  paymentModalType: PaymentModalType;
  trialInfoBlock: {
    show: boolean;
    periodMode: "day" | "week";
  };
  injectedPaymentMethods: InjectedPaymentMethod[];
  defaultInjectedPaymentMethod: InjectedPaymentMethod;
  quizPaymentModalFullMode: boolean;
  isPaymentModalShowAfterQuiz: boolean;
  errorPopup: {
    enabled: boolean;
    buttonTitle: string;
    type: ErrorPopupType;
    isDetailedErrors: boolean;
  };
  withOnlyEngFootnote: boolean;
  flow: Array<FlowStep | FlowStep[]>;
  consentCookieModal: {
    enabled: boolean;
    type: "long" | "short";
    placement: FlowStep;
  };
  isTrialPeriodReminder: boolean;
  trialsLeft: {
    badgeButtonEnabled: boolean;
    textEnabled: boolean;
    time: number;
  };
  promocodeOffer: {
    enabled: boolean;
    showPaymentModalDiscount: boolean;
    switchDefaultValue: boolean;
    products: ProductConfig[];
  };
  specialOffer: {
    // без mode switch в renderSpecialOffer не рендерит ничего — это и есть «выключено».
    // Дефолта здесь быть не должно: иначе неполный конфиг мутатора включает модалку сам.
    mode?: SpecialOfferMode;
    enabled: boolean;
    time: number;
    prevDiscount: number;
    discount: number;
    showAfterCloseAmount: number;
    products: ProductConfig[];
    scrollToTopAfterTrigger?: boolean;
    title?: LString;
    soInfoOfferText?: LString;
    guaranteedText: LString;
    guaranteedLink: LString;
    button: LocalizedString;
    guaranteedSaveText: LocalizedString;
    // читается в layouts/plans через state.remoteConfig.specialOffer
    highlightWords: string[];
  };
  noFundsOffer: NoFundsOffer;
  redirectToChrome: REDIRECT_TO_CHROME;
  isSendingTerminateLinks: boolean;
  isSendingTrialEnd: boolean;
  isTrialPriceSelection: boolean;
  chatFlow: {
    flow: Array<ChatFlow>;
    welcomeAnswerType: "single" | "multi" | "none";
    symbolsPerSecond: number;
    palmEnabled: boolean;
    redirectToSummary: boolean;
    nameStepEnabled: boolean;
    buttonText: LString;
  };
  modeCardForm: ModeCardForm;
  emailReportlLinkMode: "download" | "open" | "download+open";

  core: {
    termsInfo: {
      contact_us: LocalizedString;
      privacy_policy: LocalizedString;
      terms: LocalizedString;
      billing_terms: LocalizedString;
      money_back: LocalizedString;
    };
    quiz: Quiz;
    email: Email;
    summary: {
      plan: LocalizedString;
      image: ImageSource;
    };
    success: {
      title: {
        text: LocalizedString;
        highlightedText: Array<string>;
      };
      description: {
        text: LocalizedString;
        highlightedText: Array<string>;
      };
      footnote: LocalizedString;
      app: string;
      btn: LocalizedString;
      steps: {
        first: LocalizedString;
        second: LocalizedString;
        third: LocalizedString;
        fourth: LocalizedString;
      };
    };
  };
  paymentPopup: {
    total: {
      today: LocalizedString;
      day: LocalizedString;
      week: LocalizedString;
      total: LocalizedString;
      totalItem: LocalizedString;
    };
    selectedTitles: LocalizedString;
    trial: LocalizedString;
    editEmail: {
      title: LocalizedString;
      placeholder: LocalizedString;
      doubleCheck: LocalizedString;
      button: LocalizedString;
    };
    or: LocalizedString;
    payWithCard: LocalizedString;
    cardElementRecurly: {
      cardNumber: LocalizedString;
      mm: LocalizedString;
      yy: LocalizedString;
      firstName: LocalizedString;
      lastName: LocalizedString;
      zip: LocalizedString;
    };
    discountSave: LocalizedString;
    showPrivacy: boolean;
  };
  localizationEnabled: boolean;
  transitionVisibilityGender: "all" | "male-only" | "female-only";
  isShowedSkipButton: boolean;
  showGeneratedPhotoOnPaywall: boolean;
  upsaleOffer: {
    product: string;
    upsaleName: string;
    recurlyId: string;
    discount: string;
    textLineThrough: string;
    priceAmount: string;
  };
  showSpecialOffer: boolean;
  showFaceScan: boolean;
  preloadFaceIdModels: boolean;
  fbPixelId: number;
  routeGroups: string[][];
  upsaleFirst: {};
  skipPosition: string;
  upsaleWithoutTrial: {};
  showBeforeAfterOnPaywall: boolean;
  showPlanDescription: boolean;
  prelandText: {
    title: string;
    subTitle: string;
  };
  showImage: boolean;
  showMealPlanPaywall: boolean;
  showEmailMeals: boolean;
  showEmailReviews: boolean;
  showTwoPhones: boolean;
  addBlurDay1: boolean;
  dynamicPages: [];
  showEmailPopup: boolean;
  // Stripe-only: when enabled, an anonymous user (email = null) is created on
  // site open so checkout requires no email. The email is collected after
  // payment on the dedicated `email-after-payment` page.
  collectEmailAfterPayment: boolean;
  // Stripe deferred-email only: when enabled, the native Apple Pay sheet requests
  // the payer's email (so the email-after-payment page can be skipped when it's
  // present). NOTE: requesting it makes Apple Pay REQUIRE an email — if the user
  // has none, the sheet forces entry. Disabled => Apple Pay never asks for email
  // and the email-after-payment page is always shown.
  requestApplePayEmail: boolean;
  // The same switch under a name that does not lie: it drives every wallet sheet
  // (Apple Pay and Google Pay), not just Apple's. `requestApplePayEmail` is the
  // key that actually exists in Magnus on the funnels shipped so far, so both are
  // read — `requestWalletEmail ?? requestApplePayEmail` — and a funnel migrates
  // by simply gaining the new key. Once every project has it, the old one goes.
  //
  // Nullable on purpose: the normalizer substitutes the default for a key Magnus
  // does not have, so a `false` default would be indistinguishable from an
  // explicit `false` and would shadow the old key everywhere. `null` is what
  // makes "not configured" fall through.
  requestWalletEmail: boolean | null;
  // Stripe-only: when enabled (together with paymentSystem === stripe), the
  // native Apple Pay sheet collects the payer's billing address, which Stripe
  // returns in the payment method's `billing_details` and we attach to the
  // payment. NOTE: Stripe returns nothing unless the field is requested, so we
  // request the payer's name — the name request is what makes Apple Pay hand back
  // the billing address — but NOT phone or shipping. Email is NOT requested by
  // this flag on its own; it is only requested when `requestApplePayEmail` is
  // also enabled. Requested fields are pre-filled from the user's Apple account.
  collectApplePayBillingData: boolean;
  // TailoredPay-only: the merchant name shown inside the native Apple Pay sheet —
  // both as the payee on the total row ("Pay <name>") and as the label on the
  // recurring row. Lives in remote config rather than `core.appName` because the
  // gateway account's trading name is what has to appear there, and it differs
  // from the app's own name. Empty => falls back to `core.appName`.
  applePayMerchantName: string;
  // Sends the Facebook Pixel `Purchase` event with the amount actually charged:
  // once for the trial/subscription purchase and once for a successful upsell.
  // Disabled => no Purchase event is sent at all. Off by default, since a funnel
  // that already counts conversions elsewhere would otherwise double-count.
  sendFbPurchaseEvent: boolean;
  // Stripe-only A/B flag: when enabled (together with paymentSystem === stripe),
  // the payment button opens the hosted Stripe Checkout page (redirect) instead
  // of the custom in-app payment form. Default false → current custom form.
  useStripeCheckout: boolean;
  showGiftPopup: boolean;
  upsaleNutritionBundle: {
    product: string;
    upsaleName: string;
    recurlyId: string;
    priceAmount: string;
    movePriceToBottom: boolean;
  };
  dynamicRedirectRules: [];
  stats: {
    image: string;
    firstOption: LocalizedString;
    secondOption: LocalizedString;
  };
  guide: {
    title: LocalizedString;
    text: LocalizedString;
    blurText: LocalizedString;
  };
  tryFoodzee: {
    tryFoodzeeTitle: LocalizedString;
    // слой читает options[locale] как массив строк, а не как массив LocalizedString
    options: LocalizedArray;
    discount: {
      title: LocalizedString;
      subTitle: LocalizedString;
    };
  };
  report: {
    title: LocalizedString;
    options: Array<{
      emoji: string;
      title: LocalizedString;
      subTitle: LocalizedString;
    }>;
  };
  customersReview: Array<{
    image: string;
    title: string;
    text: LocalizedString;
  }>;
  showPlans: boolean;
  ltsPhotoExample: {
    title: LocalizedString;
    photoProgressTitle: LocalizedString;
  };
  showPhotoProgress: boolean;
  email: {
    title: LocalizedString;
    subTitle: LocalizedString;
  };
  upsaleDynamic: {
    product: string;
    upsaleName: string;
    recurlyId: string;
    priceAmount: string;
    buttonText: LocalizedString;
    title: LocalizedString;
    subTitle: LocalizedString;
    image: string;
    descriptionTitle: LocalizedString;
    descriptionText: LocalizedString;
    // страница читает whatYouGetText[locale] как массив строк, а не как массив LocalizedString
    whatYouGetText: LocalizedArray;
    bottomText: LocalizedString;
    movePriceToBottom: boolean;
    letterName: string;
    fileName: string;
  };
  timerDesign: {
    text: LocalizedString;
    buttonTitle: LocalizedString;
  };
  personalPlanPage: {
    title: LocalizedString;
    subTitle: LocalizedString;
    buttonTitle: LocalizedString;
    paymentButtonTitle: LocalizedString;
    moneyBack: {
      title: LocalizedString;
      sub1: LocalizedString;
      sub2: LocalizedString;
    };
    mealPlanTitle: LocalizedString;
    sections: string[];
  };
  giftPopup: {
    title: LocalizedString;
    buttonTitle: LocalizedString;
  };
  scrollEnabled: boolean;
  hideTimerOnScrollUp: boolean;
  sendEthnicity: string;
  showUpsalePaymentError: boolean;
  redirectToPwa: boolean;
  newPaymentModal: boolean;
  stripeSplitCardForm: boolean;
  showEmailAutocomplete: boolean;
  activeLanguages: string[];
  showGetSelfieReportPopup: boolean;
  darkBlurEnabled: boolean;
  showApplePayAnimation: boolean;
  paywall: {
    title: LocalizedString;
    options: LocalizedString[];
    noPayment: LocalizedString;
    bottomText: LocalizedString;
    product: {}
  }
}

export const INITIAL_CONFIG: RemoteConfig = {
  ...MANAGE_INITIAL_CONFIG,
  ...INITIAL_CONFIG_UPSALES,
  withOnlyEngFootnote: false,
  paymentDescriptionType: "new",
  isCvvTooltip: false,
  isOneClickFootnote: true,
  isDownloadPdfInBrowser: false,
  displayVAT: false,
  planType: PlanType.DEFAULT,
  paymentErrorRetries: {
    enabledFunds: false,
    enabled3ds: false,
    error3dsCodes: [],
  },
  paymentModalHeader: {
    title: "",
    subtitle: "",
  },
  simpleTitleModal: {
    type: "default",
    titleEnabled: true,
    text: "<b>91% found</b> their happiness with highly-personalized predictions",
    fontSize: 22,
    withTotalPriceUnder: true,
  },
  plansFeatures: [],
  consentCookieModal: {
    enabled: false,
    type: "long",
    placement: "",
  },
  flow: [],
  linkTarget: LinkTarget.DEFAULT,
  showTimerInPlanBadge: false,
  paymentProject: "",
  paymentSystem: PaymentSystem.RECURLY,
  paymentSystemReserve: "",
  products: [],
  productsDetails: [],
  additionalFields: {},
  policyLinks: POLICY_LINKS,
  address: { en: "30 N. GOULD ST STE R SHERIDAN, WY 82801" } as LocalizedString,
  buttonsConfigs: BTNS_CONFIGS,
  priceAfterTrialType: PriceAfterTrialType.FULL,
  productsTitle: { en: "Choose your plan" } as LocalizedString,
  showAfterTrialPrice: true,
  showPricesBlock: true,
  showPriceDiscount: true,
  openPaymentModalOnProductClick: true,
  isShowSafeCheckout: true,
  discountTime: 120,
  modePayPal: ModePayPal.ENABLED,
  modeApplePay: ModeApplePay.ENABLED,
  inertialApplePay: InertialApplePay.DISABLED,
  modeGooglePay: ModeGooglePay.DISABLED,
  modeMercadoPago: ModeMercadoPago.DISABLED,
  cardFormCollapsed: false,
  cardFormLayout: CardFormLayout.COMPACT,
  showPaymentSafeAtPlans: false,
  showTermsAtPlans: false,
  totalPriceType: "today",
  paymentModalEmailEnabled: true,
  paymentModalType: PaymentModalType.BASE,
  trialInfoBlock: {
    show: false,
    periodMode: "day",
  },
  isInjectedPaymentMethodOnPage: false,
  redirectToSuccessOnDecline: false,
  showPaymentProcessingOverlay: false,
  defaultInjectedPaymentMethod: InjectedPaymentMethod.CARD,
  injectedPaymentMethods: [
    InjectedPaymentMethod.APPLEPAY,
    InjectedPaymentMethod.PAYPAL,
    InjectedPaymentMethod.CARD,
  ],
  quizPaymentModalFullMode: false,
  isPaymentModalShowAfterQuiz: false,
  errorPopup: {
    enabled: false,
    buttonTitle: "",
    type: ErrorPopupType.CARD,
    isDetailedErrors: false,
  },
  isTrialPeriodReminder: false,
  trialsLeft: {
    badgeButtonEnabled: false,
    textEnabled: false,
    time: 5000,
  },
  promocodeOffer: {
    enabled: false,
    switchDefaultValue: false,
    showPaymentModalDiscount: false,
    products: [],
  },
  specialOffer: {
    enabled: false,
    time: 100,
    prevDiscount: 40,
    discount: 50,
    products: [],
    scrollToTopAfterTrigger: false,
    showAfterCloseAmount: 1,
    guaranteedLink: { en: "Learn more" },
    guaranteedText: {
      en: "In case you don’t get visible results, you can get a full refund within 30 days after purchase.",
    },
    button: { en: "Get My Report" } as LocalizedString,
    guaranteedSaveText: { en: "Guaranteed safe checkout" } as LocalizedString,
    highlightWords: [
      "SPECIAL OFFER",
      "OFERTA ESPECIAL",
      "SONDERANGEBOT",
      "OFFRE SPÉCIALE",
      "OFFERTA SPECIALE",
      "ÖZEL TEKLİF",
      "SPECIÁLNÍ NABÍDKA",
      "PENAWARAN SPESIAL",
    ],
  },
  noFundsOffer: {
    enabled: false,
    time: 100,
    discount: 50,
    products: [],
    buttonTitle: "",
  },
  redirectToChrome: REDIRECT_TO_CHROME.DISABLED,
  isSendingTerminateLinks: false,
  isSendingTrialEnd: false,
  isTrialPriceSelection: false,
  chatFlow: {
    flow: [],
    welcomeAnswerType: "none",
    symbolsPerSecond: 8,
    palmEnabled: true,
    redirectToSummary: false,
    nameStepEnabled: true,
    buttonText: "",
  },
  theme: {},
  oneClickApplePayGooglePayMode: "1click",
  modeCardForm: ModeCardForm.ENABLED,
  emailReportlLinkMode: "download",
  configVAT: {
    withGeo: true,
    view: "simple",
  },

  core: {
    termsInfo: {
      contact_us: {} as LocalizedString,
      privacy_policy: {
        en: "",
      } as LocalizedString,
      terms: {
        en: "",
      } as LocalizedString,
      billing_terms: {
        en: "",
      } as LocalizedString,
      money_back: {
        en: "",
      } as LocalizedString,
    },
    quiz: {} as Quiz,
    email: {} as Email,
    summary: {
      plan: {
        en: "Get Your Personalized\nBrain Training Plan",
      } as LocalizedString,
      image: {} as ImageSource,
    },
    success: {
      title: {
        text: {} as LocalizedString,
        highlightedText: [],
      },
      description: {
        text: {} as LocalizedString,
        highlightedText: [],
      },
      footnote: {
        en: "The links will also be duplicated in the mail.",
      } as LocalizedString,
      app: "{{appName}}",
      btn: {
        en: "Download",
      } as LocalizedString,
      steps: {
        first: {
          en: "Download {{appName}} app by using <b>the button at the bottom of the screen.</b>",
        } as LocalizedString,
        second: {
          en: "Once you have started the app, choose '{{singIn}}' (see the screenshot below).",
        } as LocalizedString,
        third: {
          en: "Enter your email and follow the instructions.",
        } as LocalizedString,
        fourth: {
          en: "After that is done, enjoy the premium features and start taking care of your loved ones!",
        } as LocalizedString,
      },
    },
  },
  paymentPopup: {
    total: {
      today: {
        en: "Total today",
      } as LocalizedString,
      day: {
        en: "Total <span>per day</span>",
      } as LocalizedString,
      week: {
        en: "Total <span>per week</span>",
      } as LocalizedString,
      total: {
        en: "Total",
      } as LocalizedString,
      totalItem: {
        en: "Item price",
      } as LocalizedString,
    },
    selectedTitles: {
      en: "You selected {{titleText}}",
    } as LocalizedString,
    trial: {
      en: "{{days}}-Day Trial",
    } as LocalizedString,
    editEmail: {
      title: {
        en: "Email",
      } as LocalizedString,
      placeholder: {
        en: "Enter your email",
      } as LocalizedString,
      doubleCheck: {
        en: "Please double-check your email address before proceeding further",
      } as LocalizedString,
      button: { en: "ok" } as LocalizedString,
    },
    or: {
      en: "or",
    } as LocalizedString,
    payWithCard: {
      en: "Pay with card",
    } as LocalizedString,
    cardElementRecurly: {
      cardNumber: {
        en: "Card number",
      } as LocalizedString,
      mm: {
        en: "MM",
      } as LocalizedString,
      yy: {
        en: "YY",
      } as LocalizedString,
      firstName: {
        en: "First Name",
      } as LocalizedString,
      lastName: {
        en: "Last Name",
      } as LocalizedString,
      zip: {
        en: "ZIP code",
      } as LocalizedString,
    },
    discountSave: { en: "SAVE {{discount}}%" } as LocalizedString,
    showPrivacy: false,
  },

  localizationEnabled: false,
  transitionVisibilityGender: "female-only",
  isShowedSkipButton: true,
  showGeneratedPhotoOnPaywall: true,
  upsaleOffer: {
    product: "",
    upsaleName: "",
    recurlyId: "",
    discount: "",
    textLineThrough: "",
    priceAmount: "",
  },
  showSpecialOffer: false,
  showFaceScan: true,
  preloadFaceIdModels: false,
  fbPixelId: 1669525607253522,
  routeGroups: [],
  upsaleFirst: {},
  skipPosition: "left",
  upsaleWithoutTrial: {},
  showBeforeAfterOnPaywall: true,
  showPlanDescription: true,
  prelandText: {
    title: "Struggling to eat healthy or lose weight?",
    subTitle: "Get a custom meal plan tailored to your needs",
  },
  showImage: true,
  showMealPlanPaywall: true,
  showEmailMeals: false,
  showEmailReviews: false,
  showTwoPhones: false,
  addBlurDay1: false,
  dynamicPages: [],
  showEmailPopup: false,
  collectEmailAfterPayment: false,
  requestApplePayEmail: false,
  // `null`, not `false` — see the type. It has to stay distinguishable from a
  // configured value for the fallback to the old key to work.
  requestWalletEmail: null,
  collectApplePayBillingData: false,
  applePayMerchantName: "Ravelo",
  sendFbPurchaseEvent: false,
  useStripeCheckout: false,
  showGiftPopup: false,
  upsaleNutritionBundle: {
    product: "",
    upsaleName: "",
    recurlyId: "",
    priceAmount: "",
    movePriceToBottom: false,
  },
  dynamicRedirectRules: [],
  stats: {
    image: "face-scan",
    firstOption: {
      en: "**1,300+** people took the quiz today",
    } as LocalizedString,
    secondOption: {
      en: "Trusted by **1.8M+** users worldwide",
    } as LocalizedString,
  },
  guide: {
    title: {
      en: "Ethnicity & Nutrition Report",
    } as LocalizedString,
    text: {
      en: "Discover how your genetics shape your nutrition and get clear, science-backed insights made easy to understand. Your report helps you finally see the patterns behind your wellness, metabolism, and daily performance.",
    } as LocalizedString,
    blurText: {
      en: "Unlock full access to read your complete report",
    } as LocalizedString,
  },
  tryFoodzee: {
    tryFoodzeeTitle: {
      en: "Your **Personalized Plan** Includes",
    } as LocalizedString,
    options: {
      en: [
        "Daily meal plan tailored to your goals",
        "AI Nutritionist available 24/7",
        "Grocery list & easy recipes",
        "Progress tracking & calorie insights",
      ],
    } as LocalizedArray,
    discount: {
      title: {
        en: "Personal discount applied",
      } as LocalizedString,
      subTitle: {
        en: "You save 95%",
      } as LocalizedString,
    },
  },
  report: {
    title: {
      en: "Why you can trust **our report**",
    } as LocalizedString,
    options: [
      {
        emoji: "💡",
        title: {
          en: "Science-backed genetic accuracy",
        } as LocalizedString,
        subTitle: {
          en: "Our testing method uses advanced genotyping technology to deliver precise, research-level insights about your ancestry, traits, and health",
        } as LocalizedString,
      },
      {
        emoji: "🌿",
        title: {
          en: "Personalised genetic insights",
        } as LocalizedString,
        subTitle: {
          en: "Your results are based entirely on your unique data — not templates or averages — giving you a detailed understanding of how your body works from the inside out",
        } as LocalizedString,
      },
      {
        emoji: "💫",
        title: {
          en: "Built to reveal your true origins & wellness patterns",
        } as LocalizedString,
        subTitle: {
          en: "Metabolism, energy, nutrition, and wellness insights — your report uncovers the genetic story that makes you unique",
        } as LocalizedString,
      },
    ],
  },
  customersReview: [
    {
      image: "tashaImg",
      title: "Tasha",
      text: {
        en: "I’ve been trying to lose weight for years, and this app finally helped me reach my goal. The personalized meal plan and food suggestions made the process simple and effective!",
      } as LocalizedString,
    },
    {
      image: "sophiaImg",
      title: "Sophia",
      text: {
        en: "Amazing experience! I lost 10 kg in just a couple of months thanks to the easy food logging and recommendations on what to cook. The app really adapted to my needs.",
      } as LocalizedString,
    },
    {
      image: "isabellaImg",
      title: "Isabella",
      text: {
        en: "I love how the app gives tips to improve my diet. Losing weight has become so much easier with nutrition based on my ethnicity type",
      } as LocalizedString,
    },
  ],
  showPlans: false,
  ltsPhotoExample: {
    title: {
      en: "Upload a selfie and get report with 97% accuracy",
    } as LocalizedString,
    photoProgressTitle: {
      en: "Analyzing your ethnicity - ",
    } as LocalizedString,
  },
  showPhotoProgress: false,
  email: {
    title: {
      en: "We predict you will reach your goal",
    } as LocalizedString,
    subTitle: {
      en: "Enter your email to continue building your plan",
    } as LocalizedString,
  },
  upsaleDynamic: {
    product: "",
    upsaleName: "",
    recurlyId: "",
    priceAmount: "",
    buttonText: {
      en: "Continue",
    } as LocalizedString,
    title: {
      en: "AI DNA Report",
    } as LocalizedString,
    subTitle: {
      en: "A closer look at your genetics",
    } as LocalizedString,
    image: "upsaleDna",
    descriptionTitle: {
      en: "🌟 50,000+ happy users",
    } as LocalizedString,
    descriptionText: {
      en: "Understand how your DNA influences allergies, disease risks, and inherited traits – with clear, AI-powered insights you can trust.",
    } as LocalizedString,
    whatYouGetText: {
      en: [
        "Allergy & sensitivity insights",
        "Genetic background & evolution",
        "Health & lifestyle risk signals",
        "Clear AI-powered explanations",
        "Science-backed context & DNA myths explained",
      ],
    } as LocalizedArray,
    bottomText: {
      en: "This is a one-time purchase. No recurring charges, no renewals - your access remains active permanently after this one-time purchase. You will receive full lifetime access to this AI DNA Report for a single payment of $29.99. Please note that this purchase is bound by FoodZee’s",
    } as LocalizedString,
    movePriceToBottom: false,
    letterName: "",
    fileName: "",
  },
  timerDesign: {
    text: {
      en: "Your Report expires in",
    } as LocalizedString,
    buttonTitle: {
      en: "Get Report",
    } as LocalizedString,
  },
  personalPlanPage: {
    title: {
      en: "Your personalized nutrition plan is ready",
    } as LocalizedString,
    subTitle: {
      en: "Based on your body data and goals, we built a meal plan that actually fits you",
    } as LocalizedString,
    buttonTitle: {
      en: "Start My Plan",
    } as LocalizedString,
    paymentButtonTitle: {
      en: "Get My Plan",
    } as LocalizedString,
    // Блок money-back: каждое поле локализовано и показывается только если
    // непустое. Пустой title → не показываем иконку и заголовок.
    moneyBack: {
      title: {
        en: "",
      } as LocalizedString,
      sub1: {
        en: "At FoodZee we carefully designed our product and strongly believe in its quality. If you don't notice any progress after using the app for at least a week, we are ready to make a complete refund within 21 days.",
        cs: "Ve FoodZee jsme náš produkt pečlivě navrhli a věříme v jeho kvalitu. Pokud po týdnu používání nevidíte žádný pokrok, jsme připraveni vrátit peníze do 21 dnů.",
        de: "Bei FoodZee haben wir unser Produkt sorgfältig gestaltet und glauben fest an seine Qualität. Wenn Sie nach mindestens einer Woche Nutzung keine Fortschritte bemerken, erstatten wir Ihnen innerhalb von 21 Tagen den vollen Betrag zurück.",
        es: "En FoodZee diseñamos cuidadosamente nuestro producto y creemos firmemente en su calidad. Si no notas progreso después de usar la app por al menos una semana, te hacemos un reembolso completo en 21 días.",
        fr: "Chez FoodZee, nous avons conçu notre produit avec soin et croyons en sa qualité. Si vous ne constatez aucun progrès après au moins une semaine d’utilisation, nous sommes prêts à vous rembourser intégralement dans un délai de 21 jours.",
        id: "Di FoodZee kami merancang produk kami dengan hati-hati dan sangat percaya pada kualitasnya. Jika Anda tidak melihat kemajuan apa pun setelah menggunakan aplikasi setidaknya selama seminggu, kami siap memberikan pengembalian dana penuh dalam waktu 21 hari.",
        it: "Da FoodZee abbiamo progettato con cura il nostro prodotto e crediamo fermamente nella sua qualità. Se non noti miglioramenti dopo almeno una settimana di utilizzo, siamo pronti a rimborsarti completamente entro 21 giorni.",
        pt: "No FoodZee, desenhamos cuidadosamente nosso produto e acreditamos fortemente em sua qualidade. Se você não notar progresso após usar o app por pelo menos uma semana, faremos um reembolso completo dentro de 21 dias.",
        tr: "FoodZee olarak ürünümüzü özenle tasarladık ve kalitesine inanıyoruz. Uygulamayı en az bir hafta kullandıktan sonra ilerleme görmezseniz, 21 gün içinde tam iade yapmaya hazırız.",
      } as LocalizedString,
      sub2: {
        en: "Find more about the applicable limitations in our Terms of Use.",
        cs: "Více o omezeních naleznete v Podmínkách užití.",
        de: "Weitere Informationen zu den geltenden Einschränkungen finden Sie in unseren Nutzungsbedingungen.",
        es: "Consulta las limitaciones aplicables en nuestros Términos de uso.",
        fr: "Consultez nos Conditions d’utilisation pour plus d’informations sur les limitations applicables.",
        id: "Pelajari lebih lanjut tentang batasan yang berlaku dalam Ketentuan Penggunaan kami.",
        it: "Trova maggiori dettagli sulle limitazioni applicabili nei nostri Termini di Utilizzo.",
        pt: "Saiba mais sobre as limitações aplicáveis em nossos Termos de Uso.",
        tr: "Uygulanabilir sınırlamalar hakkında daha fazla bilgi için Kullanım Şartlarımızı inceleyin.",
      } as LocalizedString,
    },
    // Тайтл блока meal-plan:
    //   "" (пусто)  → динамический тайтл по весу (как сейчас)
    //   "none"      → тайтл не показывается
    //   любой текст → свой тайтл (на нужной локали)
    mealPlanTitle: {
      en: "",
    } as LocalizedString,
    // Без секций страница рендерит только заголовок, поэтому дефолт — рабочий набор
    sections: [
      "goal-graph",
      "plans",
      "meal-plan",
      "button",
      "video",
      "try-foodzee",
      "payment-button",
      "joined-people",
      "button",
      "customers-review",
    ],
  },
  giftPopup: {
    title: {
      en: "You’re getting an exclusive **95% discount**",
    } as LocalizedString,
    buttonTitle: {
      en: "Continue",
    } as LocalizedString,
  },
  scrollEnabled: false,
  hideTimerOnScrollUp: false,
  sendEthnicity: "none",
  showUpsalePaymentError: true,
  redirectToPwa: false,
  newPaymentModal: false,
  stripeSplitCardForm: false,
  showEmailAutocomplete: false,
  activeLanguages: ["en"],
  showGetSelfieReportPopup: false,
  darkBlurEnabled: false,
  showApplePayAnimation: false,
  // Локали, кроме en, здесь не дублируем: незаполненный перевод откатывается
  // на en через getLocalizedText, а фейковый плейсхолдер юзер увидел бы как текст.
  paywall: {
    title: {
      en: "Eat smarter.\nGet results faster.",
    } as LocalizedString,
    options: [
      { en: "Get a personalized meal plan" } as LocalizedString,
      { en: "Track calories automatically" } as LocalizedString,
      { en: "Balance proteins, fats & carbs" } as LocalizedString,
    ],
    noPayment: {
      en: "No payment due today",
    } as LocalizedString,
    bottomText: {
      en: "By continuing, you are starting a subscription that will automatically renew at the end of the selected period unless canceled at least 24 hours before the end of the current period.\nTo avoid being charged, cancel your subscription at least 24 hours before the current period ends.\nYou can manage and cancel your subscription in your account settings.",
    } as LocalizedString,
    product: {}
  }
};
